import dayjs from "dayjs";
import { TFunction } from "i18next";
import { D2DRequestsFilters } from "../../../../api/d2d/d2dApiInterfaces";
import i18n from "../../../../i18n/i18n";
import { formatDate, formatTimeWithLocale } from "../../../../utils/formatters";
import { getD2DRejectStatusLabelKey } from "../../utils/rejectionStatus";
import { D2DOrderRow } from "../components/D2DActionMenu/D2DActionMenu";

export const isD2DRequestsFilterApplied = (filters: D2DRequestsFilters) =>
  Object.values(filters).some((value) => {
    if (Array.isArray(value)) return value.length > 0;
    return Boolean(value);
  });

export const getTooltipTextForDownloadButton = (
  rows: D2DOrderRow[],
  isFilterApplied: boolean,
  isDownloading: boolean,
  t: TFunction
) => {
  if (rows.length === 0 && !isFilterApplied) return "";
  if (isDownloading) return t("orders:download.disabled.toolTip");
  return t("orders:download.toolTip");
};

const formatDateForExport = (dateStr: string | null | undefined): string => {
  if (!dateStr) return "";
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return "";
  const formattedDate = formatDate(dateStr, i18n.language, false, true);
  if (formattedDate === "NA") return "";
  const timeLine = formatTimeWithLocale(dateStr, i18n.language);
  return timeLine ? `${formattedDate} ${timeLine}` : formattedDate;
};

const escapeCSVValue = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return "";
  const stringValue = String(value);
  if (
    stringValue.includes(",") ||
    stringValue.includes('"') ||
    stringValue.includes("\n")
  ) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }
  return stringValue;
};

export const downloadD2DRequestsAsCSV = (rows: D2DOrderRow[], t: TFunction) => {
  const getStatusLabel = (row: D2DOrderRow): string => {
    if (row.status !== "REJECTED") return row.status;
    const key =
      getD2DRejectStatusLabelKey({
        status: row.status,
        lastChangedBy: row.lastChangedBy,
        rejectionType: row.rejectionType,
        rejectionTrigger: row.rejectionTrigger,
      }) ?? "d2d:status.manualRejected";
    return t(
      key,
      key === "d2d:status.d2dClaimed" ? "D2D Claimed" : "Manual Reject"
    );
  };

  const headers = [
    t("d2d:d2dRequests.table.header.orderNumber", "Order Number"),
    t("d2d:d2dRequests.table.header.dealerReference", "Dealer Reference"),
    t("d2d:d2dRequests.table.header.partNumber", "Part Number"),
    t("d2d:d2dRequests.table.header.requestedQuantity", "Requested Qty"),
    t("d2d:d2dRequests.table.header.acceptedQuantity", "Accepted Qty"),
    t("d2d:d2dRequests.table.header.dateRequested", "Date Requested"),
    t("d2d:d2dRequests.table.header.timeToRespond", "Time To Respond"),
    t("d2d:d2dRequests.table.header.status", "Status"),
    t("d2d:d2dRequests.table.header.lastChangedBy", "Last Changed By"),
  ];

  const csvRows = rows.map((row) => [
    escapeCSVValue(row.orderNumber),
    escapeCSVValue(row.dealerReference),
    escapeCSVValue(row.partNumber),
    escapeCSVValue(row.requestedQuantity),
    escapeCSVValue(row.acceptedQuantity || "--"),
    escapeCSVValue(formatDateForExport(row.dateRequested)),
    escapeCSVValue(row.timeToRespond || "--"),
    escapeCSVValue(getStatusLabel(row)),
    escapeCSVValue(row.lastChangedBy),
  ]);

  const csvContent = [
    headers.join(","),
    ...csvRows.map((row) => row.join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const timestamp = dayjs().format("YYYY-MM-DD_HH-mm-ss");
  link.href = url;
  link.download = `D2D_Requests_${timestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
